import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useClientFromParam } from '../hooks/useClientFromParam';
import { useClientDocumentsRefresh } from '../hooks/useClientDocumentsRefresh';
import db from '@/db/db';
import { trackEvent } from '@/db/services/analytics_service';
import ClientInfoBar from '../components/ClientInfoBar';
import TabNavigation, { type TabType } from '../components/client/TabNavigation';
import ProfileTab from '../components/client/ProfileTab';
import DocumentsTab from '../components/client/DocumentsTab';
import NotesTab from '../components/client/NotesTab';
import TabErrorBoundary from '../components/client/TabErrorBoundary';
import DocumentTemplateModal from '../components/DocumentTemplateModal';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

export default function ClientDetail() {
  const { client, clientId, loading } = useClientFromParam();
  const location = useLocation();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [warning] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  // The tab lives in the URL (?tab=notes) so returning from a note lands back on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: TabType = requestedTab === 'profile' || requestedTab === 'notes' ? requestedTab : 'documents';
  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams(tab === 'documents' ? {} : { tab }, { replace: true, state: location.state });
  }, [setSearchParams, location.state]);
  const [showDocumentTemplateModal, setShowDocumentTemplateModal] = useState<boolean>(false);

  // Auto-open document template modal when navigated with openNewDocument state
  useEffect(() => {
    if ((location.state as any)?.openNewDocument) {
      // Callers navigate to the bare client URL, which opens on the Documents tab.
      setShowDocumentTemplateModal(true);
      // Clear the state so it doesn't re-trigger on subsequent renders
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Track which client we've already tracked viewing for (to prevent repeated tracking)
  const trackedClientRef = useRef<number | null>(null);

  // Update last_viewed_at when client is loaded (separate from useLiveQuery)
  useEffect(() => {
    if (client && clientId && trackedClientRef.current !== clientId) {
      // Update the client's last_viewed_at field to track when it was last viewed
      db.clients.update(clientId, { last_viewed_at: new Date().toISOString() })
        .catch(err => console.error('Failed to update last_viewed_at:', err));

      // Track client viewing (only once per client)
      trackEvent('client.viewed', {
        client_id: clientId,
      }).catch(err => {
        console.error('Failed to track client.viewed event:', err);
      });

      // Mark this client as tracked
      trackedClientRef.current = clientId;
    }
  }, [client, clientId]);

  // Mount-time refresh of the documents tab plus the pull-to-refresh handler.
  const { refresh: handleRefresh } = useClientDocumentsRefresh(clientId, activeTab);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle client not found
  useEffect(() => {
    if (client === null && !loading) {
      setError('Client not found or access denied');
    }
  }, [client, loading]);

  // Pull-to-refresh hook
  const { pullDistance, isRefreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    isEnabled: isOnline,
  });

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium mb-2">Loading client...</p>
          <p className="text-gray-600">Please wait while we fetch the client data</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Client</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Client Not Found</h2>
          <p className="text-gray-600 mb-4">The client you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.</p>
        </div>
      </div>
    );
  }

  // After all guards, client is narrowed to Client — id and slug are guaranteed
  const id = client.id;
  const slug = client.label;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <TabErrorBoundary>
            <ProfileTab client={client} clientId={id} />
          </TabErrorBoundary>
        );
      case 'notes':
        return (
          <TabErrorBoundary>
            <NotesTab clientId={id} clientSlug={slug} />
          </TabErrorBoundary>
        );
      case 'documents':
        return (
          <TabErrorBoundary>
            <DocumentsTab
              clientId={id}
              clientSlug={slug}
              onNewDocument={() => setShowDocumentTemplateModal(true)}
            />
          </TabErrorBoundary>
        );
      default:
        return (
          <TabErrorBoundary>
            <ProfileTab client={client} clientId={id} />
          </TabErrorBoundary>
        );
    }
  };

  return (
    <div className="h-full bg-gray-50">
      {/* Pull to Refresh Indicator */}
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-6">
          {/* Back Button */}
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => navigate((location.state as any)?.from ?? '/clients')}
            className="flex items-center gap-1 mb-4"
            icon="caret-left"
          >
            Back
          </Button>

          {/* Client Info Bar */}
          {client && <ClientInfoBar client={client} className="mb-6" />}

          {/* Warning Message */}
          {warning && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                  <p className="text-sm text-yellow-700 mt-1">{warning}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-6">
        {renderTabContent()}
      </div>

      {/* Document Template Modal */}
      <DocumentTemplateModal
        isOpen={showDocumentTemplateModal}
        onClose={() => setShowDocumentTemplateModal(false)}
        clientId={id}
        clientSlug={slug}
      />
    </div>
  );
}
